import { useEffect } from 'react';

/**
 * Hook to protect public document content from copying, downloading, and printing.
 * Disables: right-click, text selection, keyboard shortcuts (Ctrl+C/S/P/A, PrintScreen).
 * Hides content during print. Adds watermark-friendly overlay.
 */
const useContentProtection = (enabled = true) => {
  useEffect(() => {
    if (!enabled) return;

    const handleContextMenu = (e) => {
      e.preventDefault();
      return false;
    };

    const handleKeyDown = (e) => {
      // Block Ctrl/Cmd + C (copy), S (save), P (print), A (select all), U (view source)
      if ((e.ctrlKey || e.metaKey) && ['c', 's', 'p', 'a', 'u'].includes(e.key.toLowerCase())) {
        e.preventDefault();
        return false;
      }
      // Block PrintScreen
      if (e.key === 'PrintScreen') {
        e.preventDefault();
        return false;
      }
      // Block F12 (dev tools)
      if (e.key === 'F12') {
        e.preventDefault();
        return false;
      }
    };

    const handleDragStart = (e) => {
      e.preventDefault();
      return false;
    };

    const handleCopy = (e) => {
      e.preventDefault();
      return false;
    };

    document.addEventListener('contextmenu', handleContextMenu);
    document.addEventListener('keydown', handleKeyDown);
    document.addEventListener('dragstart', handleDragStart);
    document.addEventListener('copy', handleCopy);

    // Screenshot protection: blur content when page loses focus or visibility changes
    const protectedEl = () => document.querySelector('.protected-content');

    const handleVisibilityChange = () => {
      const el = protectedEl();
      if (!el) return;
      if (document.hidden) {
        el.style.filter = 'blur(20px)';
        el.style.transition = 'filter 0.1s';
      } else {
        // Small delay before unblurring to catch quick alt-tab screenshots
        setTimeout(() => { el.style.filter = ''; }, 300);
      }
    };

    const handleWindowBlur = () => {
      const el = protectedEl();
      if (el) {
        el.style.filter = 'blur(20px)';
        el.style.transition = 'filter 0.1s';
      }
    };

    const handleWindowFocus = () => {
      const el = protectedEl();
      if (el) {
        setTimeout(() => { el.style.filter = ''; }, 300);
      }
    };

    // Detect screen capture API (if supported)
    const handleScreenCapture = () => {
      if (navigator.mediaDevices && navigator.mediaDevices.getDisplayMedia) {
        const origGetDisplayMedia = navigator.mediaDevices.getDisplayMedia.bind(navigator.mediaDevices);
        navigator.mediaDevices.getDisplayMedia = async (...args) => {
          const el = protectedEl();
          if (el) el.style.filter = 'blur(30px)';
          try {
            return await origGetDisplayMedia(...args);
          } finally {
            if (el) setTimeout(() => { el.style.filter = ''; }, 500);
          }
        };
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('blur', handleWindowBlur);
    window.addEventListener('focus', handleWindowFocus);
    handleScreenCapture();

    // Sanitize all iframes and videos in the document
    const sanitizeMedia = () => {
      // Lock down ALL iframes in protected content (or document-content)
      document.querySelectorAll('.protected-content iframe, .document-content iframe').forEach(iframe => {
        // Block popups/navigation — no allow-popups means "open in new tab" is blocked
        iframe.setAttribute('sandbox', 'allow-scripts allow-same-origin allow-presentation');
        // Remove allow attribute that might permit popups
        iframe.removeAttribute('allow');

        const src = iframe.getAttribute('src') || '';

        // YouTube: add privacy params
        if (src.includes('youtube.com') || src.includes('youtu.be')) {
          if (!src.includes('rel=0')) {
            iframe.src = src + (src.includes('?') ? '&' : '?') + 'rel=0&modestbranding=1';
          }
        }
        // Vimeo: disable download
        if (src.includes('vimeo.com') && !src.includes('download=0')) {
          iframe.src = src + (src.includes('?') ? '&' : '?') + 'download=0';
        }

        // Wrap iframe in a container with overlay blocking the "open in new tab" button
        const parent = iframe.parentElement;
        if (parent && !parent.classList.contains('iframe-protected')) {
          parent.classList.add('iframe-protected');
          parent.style.position = 'relative';
          // Add overlay to block top-right button area (Google Drive's open-in-new-tab icon)
          if (!parent.querySelector('.iframe-overlay-blocker')) {
            const blocker = document.createElement('div');
            blocker.className = 'iframe-overlay-blocker';
            parent.appendChild(blocker);
          }
        }
      });

      // Lock down videos
      document.querySelectorAll('.protected-content video, .document-content video').forEach(video => {
        video.setAttribute('controlsList', 'nodownload noplaybackrate');
        video.setAttribute('disablePictureInPicture', 'true');
        video.oncontextmenu = () => false;
      });
    };

    // Run on mount and observe for dynamically added content
    sanitizeMedia();
    const observer = new MutationObserver(sanitizeMedia);
    const target = document.querySelector('.protected-content') || document.querySelector('.document-content');
    if (target) {
      observer.observe(target, { childList: true, subtree: true });
    }
    // Re-run after a delay for content that loads asynchronously
    const timer = setTimeout(sanitizeMedia, 2000);

    return () => {
      document.removeEventListener('contextmenu', handleContextMenu);
      document.removeEventListener('keydown', handleKeyDown);
      document.removeEventListener('dragstart', handleDragStart);
      document.removeEventListener('copy', handleCopy);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('blur', handleWindowBlur);
      window.removeEventListener('focus', handleWindowFocus);
      observer.disconnect();
      clearTimeout(timer);
      // Restore filter on unmount
      const el = protectedEl();
      if (el) el.style.filter = '';
    };
  }, [enabled]);
};

export default useContentProtection;
